import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { generateKingstonDamagePoints, getKingstonHurricaneTrack, getCriticalInfrastructure } from '../services/whatIfDamageService';
import './WhatIfMap.css';

const WhatIfMap = () => {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef([]);
  const [damagePoints, setDamagePoints] = useState([]);
  const [activeButton, setActiveButton] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize damage points
    const points = generateKingstonDamagePoints();
    setDamagePoints(points);
    setIsLoading(false);
  }, []);

  // Helper function - doesn't need useCallback as it doesn't use state
  const getCategoryColor = (category) => {
    const colors = {
      5: '#7f1d1d',
      4: '#dc2626', 
      3: '#f97316',
      2: '#facc15',
      1: '#84cc16',
      0: '#22c55e'
    };
    return colors[category] || '#6b7280';
  };

  // Helper function - doesn't need useCallback as it doesn't use state
  const getMarkerColors = (point) => {
    if (point.damageType === 'flood') {
      const colors = {
        'catastrophic': ['#1e3a8a', '#1e40af'],
        'major': ['#3b82f6', '#2563eb'], 
        'moderate': ['#60a5fa', '#3b82f6'],
        'minor': ['#93c5fd', '#60a5fa']
      };
      return colors[point.severity] || ['#dbeafe', '#93c5fd'];
    } else {
      const colors = {
        'catastrophic': ['#7f1d1d', '#991b1b'],
        'destroyed': ['#dc2626', '#b91c1c'],
        'major': ['#f97316', '#ea580c'],
        'moderate': ['#facc15', '#eab308'],
        'minor': ['#fde047', '#facc15']
      };
      return colors[point.severity] || ['#fef3c7', '#fde047'];
    }
  };

  // Helper function - doesn't need useCallback as it doesn't use state
  const createPopupContent = (point) => {
    const icon = point.damageType === 'flood' ? '💧' : '🌪️';
    const title = point.damageType === 'flood' ? 'Flood Damage' : 'Wind Damage';
    
    let details = `
      <div class="damage-popup">
        <h4>${icon} ${title}</h4>
        <p><strong>Parish:</strong> ${point.parish}</p>
        <p><strong>Severity:</strong> ${point.severity}</p>
    `;

    if (point.damageType === 'wind') {
      details += `
        <p><strong>Wind Speed:</strong> ${point.windSpeed} mph</p>
        <p><strong>Distance from Landfall:</strong> ${point.distanceFromLandfall} miles</p>
        ${point.inEyePath ? '<p><strong>🎯 In Eye Path</strong> (Double Eyewall Impact)</p>' : ''}
      `;
    } else {
      details += `
        <p><strong>Flood Type:</strong> ${point.floodType}</p>
        <p><strong>Elevation:</strong> ${Math.round(point.elevation)} ft</p>
        ${point.stormSurgeHeight > 0 ? `<p><strong>Storm Surge:</strong> ${point.stormSurgeHeight} ft</p>` : ''}
        ${point.rainfallAmount > 0 ? `<p><strong>Rainfall:</strong> ${point.rainfallAmount} inches</p>` : ''}
        ${point.isAirportArea ? '<p><strong>🛩️ Airport Zone</strong> - Critical Infrastructure</p>' : ''}
      `;
    }

    details += `
        <p><strong>Confidence:</strong> ${Math.round(point.confidence * 100)}%</p>
      </div>
    `;

    return details;
  };

  // Memoize filtering function since it depends on state
  const filterDamagePoints = useCallback(() => {
    switch (activeButton) {
      case 'wind':
        return damagePoints.filter(point => point.damageType === 'wind');
      case 'flood':
        return damagePoints.filter(point => point.damageType === 'flood');
      default:
        return damagePoints;
    }
  }, [activeButton, damagePoints]);

  // Memoize map setup functions
  const addJamaicaBoundary = useCallback((map) => {
    // Simplified Jamaica outline
    const jamaicaCoords = [
      [18.5, -78.4], [18.5, -76.2], [17.7, -76.2], [17.7, -78.4], [18.5, -78.4]
    ];
    
    L.polyline(jamaicaCoords, {
      color: '#2563eb',
      weight: 3,
      opacity: 0.8
    }).addTo(map);
  }, []);

  const addKingstonHurricaneTrack = useCallback((map) => {
    const track = getKingstonHurricaneTrack();
    const trackCoords = track.map(point => [point.lat, point.lon]);

    // Add track line
    L.polyline(trackCoords, {
      color: '#dc2626',
      weight: 4,
      opacity: 0.8,
      dashArray: '10, 5'
    }).addTo(map);

    // Add track points
    track.forEach((point, index) => {
      const isLandfall = point.landfall;
      const markerSize = isLandfall ? 18 : point.category >= 3 ? 14 : 10;
      
      const marker = L.circleMarker([point.lat, point.lon], {
        radius: markerSize,
        fillColor: getCategoryColor(point.category),
        color: isLandfall ? '#000' : '#fff',
        weight: isLandfall ? 4 : 2,
        opacity: 1,
        fillOpacity: 0.9
      }).addTo(map);

      const timeStr = new Date(point.time).toLocaleString();
      const popupContent = `
        <div class="track-popup">
          <h4>${isLandfall ? '🎯 LANDFALL - KINGSTON HARBOR' : '🌀 Hurricane Track'}</h4>
          <p><strong>Time:</strong> ${timeStr}</p>
          <p><strong>Category:</strong> ${point.category}</p>
          <p><strong>Wind Speed:</strong> ${point.windSpeed} mph</p>
          <p><strong>Pressure:</strong> ${point.pressure} mb</p>
          ${isLandfall ? '<p><strong>⚠️ Direct Hit:</strong> Capital City Devastated</p>' : ''}
        </div>
      `;
      
      marker.bindPopup(popupContent);
      
      if (isLandfall) {
        marker.openPopup();
      }
    });
  }, []);

  const addCriticalInfrastructure = useCallback((map) => {
    const infrastructure = getCriticalInfrastructure();
    
    Object.entries(infrastructure).forEach(([name, info]) => {
      // Create custom icon for infrastructure
      const iconHtml = name.includes('airport') ? '✈️' : 
                      name.includes('port') ? '🚢' : 
                      name.includes('hospital') ? '🏥' : '🏭';
      
      const customIcon = L.divIcon({
        html: `<div class="infrastructure-marker ${name}">${iconHtml}</div>`,
        className: 'custom-div-icon',
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });

      const marker = L.marker([info.lat, info.lon], { icon: customIcon }).addTo(map);

      const popupContent = `
        <div class="infrastructure-popup">
          <h4>🚨 CRITICAL INFRASTRUCTURE</h4>
          <p><strong>Facility:</strong> ${name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</p>
          <p><strong>Status:</strong> <span class="status-destroyed">${info.status}</span></p>
          <p><strong>Wind Speed:</strong> ${info.windSpeed} mph</p>
          ${info.stormSurge > 0 ? `<p><strong>Storm Surge:</strong> ${info.stormSurge} ft</p>` : ''}
          <p><strong>Elevation:</strong> ${info.elevation} ft</p>
          <div class="impact-details">
            <strong>Impact:</strong><br>
            ${info.operationalImpact}
          </div>
        </div>
      `;
      
      marker.bindPopup(popupContent);
      
      // Special handling for Norman Manley Airport
      if (name.includes('airport')) {
        marker.bindTooltip('✈️ Norman Manley Airport - DESTROYED', { 
          permanent: false,
          direction: 'top',
          className: 'airport-tooltip'
        });
      }
    });
  }, []);

  const addDamageMarkers = useCallback((map) => {
    // Clear existing markers
    markersRef.current.forEach(marker => map.removeLayer(marker));
    markersRef.current = [];

    const filteredPoints = filterDamagePoints();

    filteredPoints.forEach(point => {
      // Skip infrastructure points from regular display (they have their own markers)
      if (point.infrastructureType) return;

      const [fillColor, borderColor] = getMarkerColors(point);
      const markerSize = point.damageType === 'flood' ? 7 : 6;

      const marker = L.circleMarker([point.lat, point.lon], {
        radius: markerSize,
        fillColor: fillColor,
        color: borderColor,
        weight: 2,
        opacity: 1,
        fillOpacity: 0.8
      }).addTo(map);

      const popupContent = createPopupContent(point);
      marker.bindPopup(popupContent);

      markersRef.current.push(marker);
    });
  }, [filterDamagePoints]);

  useEffect(() => {
    if (!mapRef.current || isLoading) return;

    // Initialize Leaflet map
    const map = L.map(mapRef.current).setView([17.97, -76.79], 10); // Centered on Kingston

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    leafletMapRef.current = map;

    // Add Jamaica boundary
    addJamaicaBoundary(map);
    
    // Add hurricane track
    addKingstonHurricaneTrack(map);
    
    // Add critical infrastructure markers
    addCriticalInfrastructure(map);
    
    // Add damage points
    addDamageMarkers(map);

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, [isLoading, addJamaicaBoundary, addKingstonHurricaneTrack, addCriticalInfrastructure, addDamageMarkers]);

  useEffect(() => {
    if (leafletMapRef.current && !isLoading) {
      addDamageMarkers(leafletMapRef.current);
    }
  }, [addDamageMarkers, isLoading]);

  const handleButtonClick = (buttonType) => {
    setActiveButton(buttonType);
  };

  if (isLoading) {
    return (
      <div className="whatif-map-container">
        <div className="whatif-loading">
          <div className="whatif-spinner"></div>
          <p>Generating Kingston Harbor scenario...</p>
          <p className="loading-details">Analyzing impact on Norman Manley Airport and critical infrastructure...</p>
        </div>
      </div>
    );
  }

  const windCount = damagePoints.filter(p => p.damageType === 'wind' && !p.infrastructureType).length;
  const floodCount = damagePoints.filter(p => p.damageType === 'flood' && !p.infrastructureType).length;

  return (
    <section id="whatif-map" className="whatif-map-container">
      <div className="whatif-header">
        <h2>What If Scenario: Kingston Harbor Landfall</h2>
        <p className="whatif-description">
          Hurricane Melissa making landfall at Kingston Harbor instead of western Jamaica
        </p>
      </div>

      <div className="whatif-controls">
        <button
          className={`whatif-btn ${activeButton === 'all' ? 'active' : ''}`}
          onClick={() => handleButtonClick('all')}
        >
          All Damage
        </button>
        <button
          className={`whatif-btn ${activeButton === 'wind' ? 'active' : ''}`}
          onClick={() => handleButtonClick('wind')}
        >
          🌪️ Wind ({windCount})
        </button>
        <button
          className={`whatif-btn ${activeButton === 'flood' ? 'active' : ''}`}
          onClick={() => handleButtonClick('flood')}
        >
          💧 Flood ({floodCount})
        </button>
      </div>

      <div className="whatif-map-wrapper">
        <div ref={mapRef} className="whatif-map"></div>
        
        <div className="whatif-legend">
          <div className="whatif-legend-section">
            <h4 style={{color: 'white'}}>🌪️ Wind Damage</h4>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#7f1d1d'}}></span>
              <span>Catastrophic (157+ mph)</span>
            </div>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#dc2626'}}></span>
              <span>Destroyed (130+ mph)</span>
            </div>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#f97316'}}></span>
              <span>Major (96+ mph)</span>
            </div>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#facc15'}}></span>
              <span>Moderate (74+ mph)</span>
            </div>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#fde047'}}></span>
              <span>Minor (39+ mph)</span>
            </div>
          </div>

          <div className="whatif-legend-section">
            <h4 style={{color: 'white'}}>💧 Flood Damage</h4>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#1e3a8a'}}></span>
              <span>Catastrophic flooding</span>
            </div>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#3b82f6'}}></span>
              <span>Major flooding</span>
            </div>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#60a5fa'}}></span>
              <span>Moderate flooding</span>
            </div>
            <div className="whatif-legend-item">
              <span className="whatif-legend-color" style={{backgroundColor: '#93c5fd'}}></span>
              <span>Minor flooding</span>
            </div>
          </div>

          <div className="whatif-legend-section">
            <h4 style={{color: 'white'}}>🏗️ Critical Infrastructure</h4>
            <div className="whatif-legend-item">
              <span className="infrastructure-icon">✈️</span>
              <span>Norman Manley Airport</span>
            </div>
            <div className="whatif-legend-item">
              <span className="infrastructure-icon">🚢</span>
              <span>Kingston Port</span>
            </div>
            <div className="whatif-legend-item">
              <span className="infrastructure-icon">🏥</span>
              <span>University Hospital</span>
            </div>
          </div>
        </div>
      </div>

      <div className="whatif-scenario-info">
        <div className="whatif-info-box">
          <h3>Scenario Overview</h3>
          <div className="whatif-stats">
            <div className="whatif-stat">
              <strong>Landfall Location:</strong> Kingston Harbor (17.97°N, 76.79°W)
            </div>
            <div className="whatif-stat">
              <strong>Category at Landfall:</strong> 5 (166 mph winds)
            </div>
            <div className="whatif-stat">
              <strong>Most Affected Parishes:</strong> Kingston, St. Andrew, St. Catherine
            </div>
            <div className="whatif-stat">
              <strong>Storm Surge:</strong> Up to 22 feet in harbor areas
            </div>
            <div className="whatif-stat">
              <strong>Total Damage Points:</strong> {(windCount + floodCount).toLocaleString()}
            </div>
            <div className="whatif-stat critical">
              <strong>✈️ Norman Manley Airport:</strong> Complete Destruction - 18 ft storm surge, 166 mph winds
            </div>
          </div>
        </div>

        <div className="whatif-comparison">
          <h3>Key Differences from Actual Path</h3>
          <ul>
            <li>🎯 <strong>Direct hit on capital:</strong> Kingston and Spanish Town devastated</li>
            <li>🌊 <strong>Massive storm surge:</strong> Kingston Harbor amplifies coastal flooding to 22 feet</li>
            <li>✈️ <strong>Airport destroyed:</strong> Norman Manley International completely submerged and destroyed</li>
            <li>🏢 <strong>Economic impact:</strong> Financial district and port facilities obliterated</li>
            <li>🏥 <strong>Healthcare crisis:</strong> University Hospital severely damaged, medical system collapsed</li>
            <li>🛣️ <strong>Transportation:</strong> All major highways into Kingston impassable</li>
            <li>⚡ <strong>Power grid:</strong> National electrical infrastructure centered in Kingston destroyed</li>
            <li>📡 <strong>Communications:</strong> Telecom infrastructure lost, national communications down</li>
            <li>🏦 <strong>Banking system:</strong> Major banks headquarters destroyed, financial system paralyzed</li>
            <li>🎓 <strong>Education:</strong> University of the West Indies campus severely damaged</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default WhatIfMap;
